// packages/console/src/components/PosterSlot.tsx
//
// Poster-frame slot for the source inspector. The panel map maps the poster to
// FT-3 GET /v1/jobs/{id}/poster (the auto-generated scene-aware frame). Now
// that the orchestrator exposes that route, this slot fetches the server poster
// for the selected job and shows it as the default. The prototype's
// user-fillable behavior is preserved and TAKES PRECEDENCE: a dropped or
// browsed still overrides the server frame and is persisted locally, keyed by
// jobId, so it survives reloads. Precedence is override-first, then the server
// poster, then the placeholder; a missing server poster (404) is not an error,
// it just leaves the placeholder until one exists.

import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icons";
import { fetchPoster } from "../api/jobs";

const STORE_PREFIX = "aether.console.poster.";

function loadPoster(jobId: string): string | null {
  try {
    return localStorage.getItem(STORE_PREFIX + jobId);
  } catch {
    return null;
  }
}

function savePoster(jobId: string, dataUrl: string): void {
  try {
    localStorage.setItem(STORE_PREFIX + jobId, dataUrl);
  } catch {
    // Storage full or unavailable: the poster simply does not persist.
  }
}

export function PosterSlot({ jobId, placeholder }: { jobId: string | null; placeholder: string }) {
  // override is the user-supplied still (persisted locally); serverPoster is
  // the object URL of the FT-3 auto-generated poster. The override wins.
  const [override, setOverride] = useState<string | null>(null);
  const [serverPoster, setServerPoster] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setOverride(jobId ? loadPoster(jobId) : null);
  }, [jobId]);

  // Fetch the server poster for the selected job. Guarded by cancellation so a
  // rapid job switch does not show a stale poster, and the object URL is
  // revoked on cleanup to avoid leaking blobs.
  useEffect(() => {
    if (!jobId) {
      setServerPoster(null);
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchPoster(jobId, controller.signal)
      .then((url) => {
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setServerPoster(url);
      })
      .catch(() => {
        // A transient fetch failure leaves the placeholder (or an existing
        // override) rather than fabricating a frame.
        if (!cancelled) setServerPoster(null);
      });
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [jobId]);

  const displayUrl = override ?? serverPoster;

  const accept = (file: File | undefined) => {
    if (!file || !jobId || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      setOverride(url);
      savePoster(jobId, url);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        accept(e.dataTransfer.files[0]);
      }}
      style={{
        position: "absolute",
        inset: 0,
        cursor: "pointer",
        display: "grid",
        placeItems: "center",
        background: over ? "var(--blue-tint)" : "transparent",
        border: over ? "1px dashed var(--blue-500)" : "none",
      }}
    >
      {displayUrl ? (
        <img src={displayUrl} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, color: "var(--fg4)", pointerEvents: "none" }}>
          <Icon name="upload" size={16} color="var(--fg4)" />
          <span style={{ font: "var(--t-micro)", color: "var(--fg4)", textAlign: "center", padding: "0 10px" }}>{placeholder}</span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => accept(e.target.files?.[0] ?? undefined)}
      />
    </div>
  );
}
